import React from 'react';
import moment from 'moment';
import { readDate } from '../../utils/appDate';
import { T, NUMERIC, balanceColor, money } from './customers.theme';
import {
  CalendarIcon,
  Dialog,
  DialogHeader,
  ErrorLine,
  PillField,
  PrimaryButton,
} from './CustomersUI';

/**
 * Bixin and Deen Cusub — a payment in, or a debt on.
 *
 * One dialog for both. The only difference is the title, and which side of the
 * transaction the amount lands on: `credit` for a payment, `debit` for a
 * charge. The server does the arithmetic; this only decides the sign.
 *
 * Three fields, in the phone's order: amount, description, date. The employee
 * name field and the over-limit password step the phone has are deliberately
 * not here — a plain flow was asked for, so a charge goes on whatever the
 * customer's limit says. The limit is still shown beside the balance, as
 * information rather than a gate.
 *
 * The same dialog corrects a line already in the book. What it does NOT let
 * you change is which side it sits on: turning a charge into a payment is a
 * different entry, not a correction, and doing it by accident moves the
 * balance by twice the amount.
 */

const DATE_FORMAT = 'DD/MMM/YYYY';

export default function TransactionDialog({
  kind,
  customer,
  currency,
  transaction,
  onClose,
  onSubmit,
}) {
  const editing = Boolean(transaction);
  const isPayment = kind === 'payment';

  const [amount, setAmount] = React.useState(() => {
    if (!transaction) return '';
    const value = Number(transaction.debit || 0) > 0 ? transaction.debit : transaction.credit;
    return String(value ?? '');
  });
  const [description, setDescription] = React.useState(
    () => (transaction && transaction.description) || ''
  );
  const [date, setDate] = React.useState(() =>
    transaction
      ? readDate(transaction.date).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD')
  );
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const submit = async () => {
    if (busy) return;

    const value = Number(amount);
    if (!amount.trim() || Number.isNaN(value) || value <= 0) {
      setError('Enter an amount greater than zero');
      return;
    }

    setBusy(true);
    setError('');

    try {
      await onSubmit({
        kind: isPayment ? 'payment' : 'charge',
        amount: value,
        // The phone lets this be blank and writes the card's name instead;
        // same here, so a hurried entry still says what it was.
        description: description.trim() || (isPayment ? 'Bixin' : 'Deen Cusub'),
        date,
        transaction,
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not save. Try again.');
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader
          title={
            editing ? 'Edit Transaction' : isPayment ? 'Payment Card' : 'Debts Card'
          }
          onClose={onClose}
          locked={busy}
        />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 0 4px',
          }}
        >
          <span style={{ fontSize: 13, color: T.inkSoft }}>{customer?.name} owes</span>
          <span
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: balanceColor(customer?.balance),
              ...NUMERIC,
            }}
          >
            {money(customer?.balance, currency)}
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 14 }}>
          <PillField
            name="transaction-amount"
            type="number"
            value={amount}
            onChange={(value) => {
              setAmount(value);
              setError('');
            }}
            onEnter={submit}
            placeholder="Geli Lacagta"
            disabled={busy}
            invalid={Boolean(error)}
            autoFocus
          />

          <PillField
            name="transaction-description"
            value={description}
            onChange={setDescription}
            onEnter={submit}
            placeholder="Geli Faahfaahinta"
            disabled={busy}
          />

          {/* A real date input: the phone opens a picker, and a desktop
              already has one built into the browser. */}
          <PillField
            name="transaction-date"
            type="date"
            value={date}
            onChange={setDate}
            disabled={busy}
            trailing={
              <span style={{ display: 'flex', color: T.inkFaint }}>
                <CalendarIcon />
              </span>
            }
          />
        </div>

        <div style={{ paddingTop: 16 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <PrimaryButton
          onClick={submit}
          busy={busy}
          style={{ width: '100%', marginTop: error ? 12 : 20 }}
        >
          {editing ? 'Update Now' : 'Xarey'}
        </PrimaryButton>

        {editing ? null : (
          <div
            style={{
              fontSize: 11.5,
              color: T.inkFaint,
              lineHeight: 1.5,
              textAlign: 'center',
              paddingTop: 14,
            }}
          >
            {isPayment
              ? `This will reduce what ${customer?.name} owes`
              : `This will increase what ${customer?.name} owes`}
          </div>
        )}
      </div>
    </Dialog>
  );
}

export { DATE_FORMAT };
